import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { makeStyles } from '@material-ui/core'
import axios from 'axios'

import { isRequired, isValidEmail } from 'utils/formValidations'
import history from 'utils/getHistory'
import * as metrics from 'utils/metrics'

import { Form, FormInput } from 'components/forms'
import Button from 'components/virtual/buttons/Button'
import Text from 'components/texts/Text'

import { ReactComponent as GoogleIcon } from 'images/icons/social-icons/google-icon.svg'
import { ReactComponent as LinkedinIcon } from 'images/icons/social-icons/linkedin-icon.svg'

import ErrorBox from './ErrorBox'

const useStyles = makeStyles(theme => ({
  authButton: {
    marginBottom: 12,
  },
  authButtons: {
    marginBottom: 24,
    width: '100%',
  },
  blurb: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
  },
  divider: {
    backgroundColor: theme.colors.skyDarker,
    marginBottom: 24,
    height: 1,
    width: '100%',
  },
  icon: {
    marginRight: 12,
  },
  input: {
    marginBottom: 18,
  },
  marginBottom12: {
    marginBottom: 12,
  },
  submitButton: {
    marginBottom: 24,
  },
  title: {
    marginBottom: 28,
  },
}))

const track = action => () => {
  metrics.create('loginFlow', {
    meta: {
      page: 'userLogin',
      action,
    },
  })
}

function LoginForm() {
  const classes = useStyles()

  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  const redirect = () => {
    const { search, state } = history.location
    let path = '/'
    if (state && state.to) {
      path = state.to
    } else if (search && search.startsWith('?to=')) {
      path = decodeURIComponent(search.slice(4))
    }
    window.location.replace(`/app${path}`)
  }

  const login = async ({ email, password }) => {
    setLoading(true)
    setError('')
    try {
      await axios.post('/login', {
        email,
        password,
      })
      setLoading(false)
      redirect()
    } catch (err) {
      setError(err.response.data || 'Invalid Email or Password')
      setLoading(false)
    }
  }

  const loginGoogle = async response => {
    setLoading(true)
    setError('')
    if (response.error) {
      setError(response.error)
      return
    }
    try {
      await axios.post('/login/google', {
        idtoken: response.getAuthResponse().id_token,
      })
      redirect()
    } catch (err) {
      setError(err.response.data || 'Google Sign In Invalid')
      window.gapi.auth2.getAuthInstance().signOut()
    }
    setLoading(false)
  }

  const signinLinkedIn = () => {
    track('openedLinkedIn')()
    window.open('/login/linkedin', 'linkedin_window', 'height=600,width=600')
  }

  const signinGoogle = response => {
    track('openedGoogle')()
    if (response) {
      response.preventDefault()
    }
    const GoogleAuth = window.gapi.auth2.getAuthInstance()
    const options = {
      onfailure: loginGoogle,
      onsuccess: loginGoogle,
      prompt: 'select_account',
      scope: 'profile email',
    }
    GoogleAuth.signIn(options)
      .then(res => {
        loginGoogle(res)
      })
      .catch(err => {
        loginGoogle(err)
      })
  }

  useEffect(() => {
    const linkedinListener = msg => {
      if (msg && msg.data && msg.data.linkedInSuccess) {
        redirect()
      } else if (msg && msg.data && msg.data.linkedInSuccess === false) {
        setError(msg.data.error)
      }
    }
    window.addEventListener('message', linkedinListener)
    return () => {
      window.removeEventListener('message', linkedinListener)
    }
  }, [])

  return (
    <>
      <div className={classes.title}>
        <Text size={34}>Log in to your account</Text>
      </div>
      {!!error && <ErrorBox value={error} />}
      <Form initialValues={{ email: '', password: '' }} onSubmit={login} withSubmit="signIn">
        <div className={classes.input}>
          <FormInput
            compact
            disableErrorText
            fullWidth
            label="Work Email"
            name="email"
            theme="virtual"
            type="text"
            useComponentError
            validateFields={['email']}
            validations={[isRequired, isValidEmail]}
          />
        </div>
        <div className={classes.input}>
          <FormInput
            compact
            disableErrorText
            fullWidth
            label="Password"
            name="password"
            theme="virtual"
            type="password"
            useComponentError
            validateFields={['password']}
            validations={[isRequired]}
          />
        </div>
      </Form>
      <div className={classes.submitButton}>
        <Button
          color="primary"
          disabled={loading}
          fullWidth
          id="signIn"
          label="Log in"
          size="medium"
          type="contained"
        />
      </div>
      <div className={classes.divider} />
      <div className={classes.authButtons}>
        <div className={classes.authButton}>
          <Button
            color="default"
            fullWidth
            icon={<GoogleIcon />}
            label="Log in with Google"
            onClick={signinGoogle}
            size="medium"
            type="outlined"
          />
        </div>
        <div className={classes.authButton}>
          <Button
            color="default"
            fullWidth
            icon={<LinkedinIcon />}
            label="Log in with LinkedIn"
            onClick={signinLinkedIn}
            size="medium"
            type="outlined"
          />
        </div>
      </div>
      <div className={classes.blurb}>
        <div className={classes.marginBottom12}>
          <Text size={14}>
            Don’t have an account?
            <Link to="/register"> Sign Up </Link>
          </Text>
        </div>
        <Text size={14}>
          <Link to="/forgot-password"> Forgot Password </Link>
        </Text>
      </div>
    </>
  )
}

export default React.memo(LoginForm)
